/**
 * Chiamate alle API del servizio `agents`: set, agenti, ruoli, progetti del
 * set, caricamento delle cartelle, conversazione (ADR 0008 §3, §7) e hosting
 * dei progetti sul cluster (ADR 0010 §6).
 *
 * I tipi stanno in `agentTypes.ts` e sono riesportati da qui, così chi usa il
 * client ha un solo punto da cui importare.
 *
 * Provvisorio dichiarato: questi percorsi sono scritti a mano mentre CLAUDE.md
 * §2.3 impone di generarli dagli schemi OpenAPI. `agents.test.ts` presidia
 * metodo, percorso e corpo di ogni chiamata.
 */

import { GuidedRequestsApi } from './guidedRequests';
import type { UsersUsage } from './overviewTypes';
import type { Consumption } from './consumptionTypes';
import type { MyUsage, SetUsage, UserUsage } from './usageTypes';
import type {
  AgentRole,
  AgentSet,
  AgentSetChanges,
  AgentSetStatus,
  AgentSummary,
  DesiredState,
  ExternalHostingRequest,
  HostingEnvelope,
  MessageAttachment,
  MessagesPage,
  NewAgent,
  NewAgentSet,
  SetCapabilities,
  SetMessage,
  SetProject,
  SetProjectRequest,
  UploadAccepted,
} from './agentTypes';

export type * from './agentTypes';
export * from './projectModeTypes';
export * from './guidedRequests';

/** Dimensione massima di un archivio caricato: 50 MiB (ADR 0008 §3.4). */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** Lunghezza massima del testo di un messaggio, in byte UTF-8 (ADR 0008 §7). */
export const MAX_MESSAGE_BYTES = 32 * 1024;

/** Allegati al più per messaggio (ADR 0016). */
export const MAX_ATTACHMENTS = 10;

/** Dimensione massima di un allegato: 20 MiB (ADR 0016). */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** Opzioni di lettura della conversazione. */
export interface MessagesQuery {
  /** Revisione dopo la quale leggere i cambiamenti; omessa, gli ultimi messaggi. */
  after?: number;
  /** Numero massimo di messaggi. */
  limit?: number;
}

/** Opzioni del caricamento di un archivio. */
export interface UploadProgressOptions {
  /**
   * Invocata a ogni avanzamento.
   *
   * @param loaded Byte inviati.
   * @param total Byte totali.
   */
  onProgress?: (loaded: number, total: number) => void;
  /** Segnale per interrompere il caricamento. */
  signal?: AbortSignal;
}

/** Dati facoltativi di un messaggio oltre al testo. */
export interface SendMessageExtra {
  /** Allegati già caricati con `uploadAttachment`, al più 10. */
  attachmentIds?: readonly string[];
  /** Progetto del set a cui si riferisce il messaggio (ADR 0018 §4); omesso, tutti. */
  projectId?: string;
}

/**
 * Percorso della collezione dei set, o di un set.
 *
 * @param setId Set; omesso, la collezione.
 * @returns Il percorso relativo al gateway.
 */
function setsPath(setId?: string): string {
  return setId === undefined
    ? '/api/v1/agent-sets'
    : `/api/v1/agent-sets/${encodeURIComponent(setId)}`;
}

/**
 * Percorso dell'hosting di un progetto visto da un set.
 *
 * @param setId Set che contiene il progetto.
 * @param projectId Progetto.
 * @returns Il percorso relativo al gateway.
 */
function hostingPath(setId: string, projectId: string): string {
  return `${setsPath(setId)}/projects/${encodeURIComponent(projectId)}/hosting`;
}

/**
 * Operazioni del servizio `agents`, esposte alla UI. Il costruttore (il client HTTP verso
 * il gateway) e gli incarichi guidati di ADR 0035 vengono da `GuidedRequestsApi`.
 */
export class AgentsApi extends GuidedRequestsApi {
  /**
   * Elenca i ruoli disponibili per gli agenti.
   *
   * @returns Le voci del catalogo.
   */
  listRoles(): Promise<AgentRole[]> {
    return this.client.request<AgentRole[]>('GET', '/api/v1/agent-roles');
  }

  /**
   * Elenca i set di agenti del chiamante; tutti per un `ADMIN` (ADR 0023 §3).
   *
   * @returns I set, archiviati compresi.
   */
  listSets(): Promise<AgentSet[]> {
    return this.client.request<AgentSet[]>('GET', setsPath());
  }

  /**
   * Legge piano, limiti e set posseduti del chiamante (ADR 0023 §3).
   *
   * @returns Le facoltà sui set.
   */
  getCapabilities(): Promise<SetCapabilities> {
    return this.client.request<SetCapabilities>('GET', '/api/v1/agent-sets/capabilities');
  }

  /**
   * Legge un set.
   *
   * @param setId Set da leggere.
   * @returns Il set, con runtime, relay e credenziali.
   */
  getSet(setId: string): Promise<AgentSet> {
    return this.client.request<AgentSet>('GET', setsPath(setId));
  }

  /**
   * Crea un set. Il backend crea anche il relay, fermo.
   *
   * @param set Dati del nuovo set.
   * @returns Il set creato.
   */
  createSet(set: NewAgentSet): Promise<AgentSet> {
    return this.client.request<AgentSet>('POST', setsPath(), set);
  }

  /**
   * Cambia nome, descrizione, taglia o profilo di un set.
   *
   * @param setId Set da modificare.
   * @param changes Campi da cambiare.
   * @returns Il set aggiornato.
   */
  updateSet(setId: string, changes: AgentSetChanges): Promise<AgentSet> {
    return this.client.request<AgentSet>('PATCH', setsPath(setId), changes);
  }

  /**
   * Attiva, sospende o archivia un set. Sospensione e archiviazione fermano gli agenti.
   *
   * @param setId Set da aggiornare.
   * @param status Stato richiesto.
   * @returns Il set aggiornato.
   */
  setStatus(setId: string, status: Exclude<AgentSetStatus, 'draft'>): Promise<AgentSet> {
    return this.client.request<AgentSet>('POST', `${setsPath(setId)}/status`, { status });
  }

  /**
   * Elimina un set, per esempio creato per errore: agenti fermati, conversazione, task e
   * siti non usati da altri set tolti; i progetti restano.
   *
   * @param setId Set da eliminare.
   */
  deleteSet(setId: string): Promise<void> {
    return this.client.request<void>('DELETE', setsPath(setId));
  }

  /**
   * Riavvia il set: il pod si ferma e riparte con gli strumenti e le
   * credenziali correnti (ADR 0008, Appendice B).
   *
   * @param setId Set da riavviare.
   * @returns Il set aggiornato.
   */
  restartSet(setId: string): Promise<AgentSet> {
    return this.client.request<AgentSet>('POST', `${setsPath(setId)}/restart`);
  }

  /**
   * Elenca gli agenti di un set, relay compreso.
   *
   * @param setId Set di riferimento.
   * @returns Gli agenti del set.
   */
  listAgents(setId: string): Promise<AgentSummary[]> {
    return this.client.request<AgentSummary[]>('GET', `${setsPath(setId)}/agents`);
  }

  /**
   * Aggiunge un agente a un set.
   *
   * @param setId Set di destinazione.
   * @param agent Ruolo, nome, profilo e harness.
   * @returns L'agente creato.
   */
  addAgent(setId: string, agent: NewAgent): Promise<AgentSummary> {
    return this.client.request<AgentSummary>('POST', `${setsPath(setId)}/agents`, agent);
  }

  /**
   * Avvia o ferma un agente.
   *
   * @param agentId Agente interessato.
   * @param state Stato desiderato.
   * @returns L'agente aggiornato.
   */
  setDesiredState(agentId: string, state: DesiredState): Promise<AgentSummary> {
    return this.client.request<AgentSummary>(
      'POST',
      `/api/v1/agents/${encodeURIComponent(agentId)}/desired-state`,
      { state },
    );
  }

  /**
   * Elenca i progetti di un set con lo stato della loro importazione.
   *
   * @param setId Set di riferimento.
   * @returns I progetti del set.
   */
  listSetProjects(setId: string): Promise<SetProject[]> {
    return this.client.request<SetProject[]>('GET', `${setsPath(setId)}/projects`);
  }

  /**
   * Crea un progetto nel set o ne collega uno esistente.
   *
   * Collegare **crea permessi reali** per gli agenti del set, a nome
   * dell'utente: il backend rifiuta se l'utente non può concederli.
   *
   * @param setId Set di destinazione.
   * @param request Creazione o collegamento.
   * @returns Il progetto come appare nel set.
   */
  addSetProject(setId: string, request: SetProjectRequest): Promise<SetProject> {
    return this.client.request<SetProject>('POST', `${setsPath(setId)}/projects`, request);
  }

  /**
   * Approva o rifiuta l'analisi di un progetto `delegated` (ADR 0024 §3).
   * Approvata, il set può scrivere sul repository e il progetto riceve l'hosting.
   *
   * @param setId Set del progetto.
   * @param projectId Progetto in affidamento totale.
   * @param choice `approve` o `reject`.
   * @returns Il progetto aggiornato.
   */
  decideAnalysis(setId: string, projectId: string, choice: 'approve' | 'reject'): Promise<SetProject> {
    return this.client.request<SetProject>(
      'POST',
      `${setsPath(setId)}/projects/${encodeURIComponent(projectId)}/analysis/${choice}`,
    );
  }

  /**
   * Mostra o nasconde il progetto nella vetrina della landing page di
   * institorAI (ADR 0026). Solo il proprietario del set o un `ADMIN`.
   *
   * @param setId Set del progetto.
   * @param projectId Progetto.
   * @param visible True per mostrarlo, false per toglierlo.
   * @returns Il progetto aggiornato.
   * @throws ApiError 404 se il progetto non è nel set o l'utente non può cambiarlo.
   */
  setShowcase(setId: string, projectId: string, visible: boolean): Promise<SetProject> {
    return this.client.request<SetProject>(
      'PUT',
      `${setsPath(setId)}/projects/${encodeURIComponent(projectId)}/showcase`,
      { visible },
    );
  }

  /**
   * Toglie un progetto dal set. I file nel volume li cancella il supervisore.
   *
   * @param setId Set di riferimento.
   * @param projectId Progetto da togliere.
   * @returns Nulla.
   */
  removeSetProject(setId: string, projectId: string): Promise<void> {
    return this.client.request<void>(
      'DELETE',
      `${setsPath(setId)}/projects/${encodeURIComponent(projectId)}`,
    );
  }

  /**
   * Carica l'archivio zip di una cartella in un progetto del set.
   *
   * @param setId Set di riferimento.
   * @param projectId Progetto di destinazione.
   * @param archive Archivio zip, al massimo `MAX_UPLOAD_BYTES`.
   * @param options Avanzamento e interruzione.
   * @returns La ricevuta del caricamento, in attesa di estrazione.
   */
  uploadProject(
    setId: string,
    projectId: string,
    archive: Blob,
    options: UploadProgressOptions = {},
  ): Promise<UploadAccepted> {
    return this.client.upload<UploadAccepted>(
      'PUT',
      `${setsPath(setId)}/projects/${encodeURIComponent(projectId)}/upload`,
      archive,
      { contentType: 'application/zip', ...options },
    );
  }

  /**
   * Legge la conversazione con il set.
   *
   * Con `after` restituisce solo i messaggi cambiati e gli eventi nuovi dopo
   * quella revisione: è ciò che rende economica l'interrogazione periodica.
   *
   * @param setId Set di riferimento.
   * @param query Revisione di partenza e limite.
   * @returns Messaggi, eventi e revisione corrente.
   */
  listMessages(setId: string, query: MessagesQuery = {}): Promise<MessagesPage> {
    const params = new URLSearchParams();
    if (query.after !== undefined) {
      params.set('after', String(query.after));
    }
    if (query.limit !== undefined) {
      params.set('limit', String(query.limit));
    }
    const suffix = params.toString() === '' ? '' : `?${params.toString()}`;
    return this.client.request<MessagesPage>('GET', `${setsPath(setId)}/messages${suffix}`);
  }

  /**
   * Invia un messaggio al set; senza destinatario va al relay.
   *
   * @param setId Set di riferimento.
   * @param text Testo, al massimo `MAX_MESSAGE_BYTES`.
   * @param agentId Destinatario, se diverso dal relay.
   * @param extra Allegati già caricati e progetto a cui si riferisce il messaggio (ADR 0018 §4).
   * @returns Il messaggio in coda.
   */
  sendMessage(setId: string, text: string, agentId?: string, extra: SendMessageExtra = {}): Promise<SetMessage> {
    const body: Record<string, unknown> = { text };
    if (agentId !== undefined) {
      body.agent_id = agentId;
    }
    if (extra.attachmentIds !== undefined && extra.attachmentIds.length > 0) {
      body.attachment_ids = extra.attachmentIds;
    }
    if (extra.projectId !== undefined) {
      body.project_id = extra.projectId;
    }
    return this.client.request<SetMessage>('POST', `${setsPath(setId)}/messages`, body);
  }

  /**
   * Carica un allegato per un messaggio successivo (ADR 0016): i byte nel corpo, il tipo
   * in `Content-Type`, il nome percent-encoded in `X-Filename`.
   *
   * @param setId Set di riferimento.
   * @param file Contenuto, al massimo 20 MiB.
   * @param name Nome del file.
   * @param options Avanzamento e interruzione.
   * @returns I metadati dell'allegato, con l'id da citare nel messaggio.
   * @throws ApiError 409 `attachment_too_large` oltre 20 MiB.
   */
  uploadAttachment(
    setId: string,
    file: Blob,
    name: string,
    options: UploadProgressOptions = {},
  ): Promise<MessageAttachment> {
    return this.client.upload<MessageAttachment>('POST', `${setsPath(setId)}/attachments`, file, {
      contentType: file.type === '' ? 'application/octet-stream' : file.type,
      headers: { 'X-Filename': encodeURIComponent(name) },
      ...options,
    });
  }

  /**
   * Annulla un messaggio in coda o in corso.
   *
   * @param setId Set di riferimento.
   * @param messageId Messaggio da annullare.
   * @returns Il messaggio aggiornato.
   */
  cancelMessage(setId: string, messageId: string): Promise<SetMessage> {
    return this.client.request<SetMessage>(
      'POST',
      `${setsPath(setId)}/messages/${encodeURIComponent(messageId)}/cancel`,
    );
  }

  /**
   * Rimanda all'orchestratore una richiesta a cui l'Institore ha risposto da solo: il
   * backend crea un messaggio nuovo con l'intestazione della piattaforma.
   *
   * @param setId Set di riferimento.
   * @param messageId Richiesta con la risposta diretta.
   * @returns Il messaggio nuovo, in coda.
   */
  forwardMessage(setId: string, messageId: string): Promise<SetMessage> {
    return this.client.request<SetMessage>(
      'POST',
      `${setsPath(setId)}/messages/${encodeURIComponent(messageId)}/forward`,
    );
  }

  /**
   * Legge l'ambiente di hosting di un progetto del set (ADR 0010 §6).
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto.
   * @returns `{hosting: null}` se l'ambiente non esiste, altrimenti il suo stato.
   */
  getHosting(setId: string, projectId: string): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('GET', hostingPath(setId, projectId));
  }

  /**
   * Crea l'ambiente di hosting del progetto. Serve l'accesso in scrittura del
   * set: il backend rifiuta con 409 `read_only_project` o `hosting_exists`.
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto da pubblicare.
   * @returns Lo stato dell'ambiente appena creato.
   */
  createHosting(setId: string, projectId: string): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', hostingPath(setId, projectId));
  }

  /**
   * Cancella l'ambiente di hosting: namespace e tutto ciò che contiene.
   *
   * Il nome host viaggia nel corpo come conferma, e il backend lo ricontrolla.
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto.
   * @param confirmHostname Nome host del sito, scritto dall'utente.
   * @returns Nulla; lo stato si rilegge con `getHosting`.
   */
  deleteHosting(setId: string, projectId: string, confirmHostname: string): Promise<void> {
    return this.client.request<void>('DELETE', hostingPath(setId, projectId), {
      confirm_hostname: confirmHostname,
    });
  }

  /**
   * Riaccende il sito in pausa per inattività (ADR 0025 §4.2), come farebbe la
   * prima visita.
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto.
   * @returns L'ambiente; `sleep_state` è `waking` finché il sito non è pronto.
   */
  wakeHosting(setId: string, projectId: string): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', `${hostingPath(setId, projectId)}/wake`);
  }

  /**
   * Spegne il sito per scelta del proprietario (risposta B3 del 2026-10-11): carichi a zero
   * e pagina «sito spento»; dati e ambiente restano. Le visite non lo riaccendono.
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto.
   * @returns L'ambiente con `sleep_state` `disabled`.
   */
  disableHosting(setId: string, projectId: string): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', `${hostingPath(setId, projectId)}/disable`);
  }

  /**
   * Riaccende il sito spento dal proprietario.
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto.
   * @returns L'ambiente; `sleep_state` è `waking` finché il sito non è pronto.
   */
  enableHosting(setId: string, projectId: string): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', `${hostingPath(setId, projectId)}/enable`);
  }

  /**
   * Collega uno spazio esistente sul cluster come hosting del progetto (ADR 0038).
   * Solo per gli amministratori: il backend rifiuta gli altri con 403, i nomi di
   * sistema con 422 `namespace_forbidden`.
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto.
   * @param request Namespace, sito, scrittura e sua conferma.
   * @returns Lo stato del collegamento, con `kind: external`.
   */
  linkExternalHosting(setId: string, projectId: string, request: ExternalHostingRequest): Promise<HostingEnvelope> {
    return this.client.request<HostingEnvelope>('POST', `${hostingPath(setId, projectId)}/external`, request);
  }

  /**
   * Scollega lo spazio esistente: toglie solo gli accessi dati dalla piattaforma.
   *
   * @param setId Set che contiene il progetto.
   * @param projectId Progetto.
   * @returns Nulla; lo stato si rilegge con `getHosting`.
   */
  unlinkExternalHosting(setId: string, projectId: string): Promise<void> {
    return this.client.request<void>('DELETE', `${hostingPath(setId, projectId)}/external`);
  }

  /**
   * Consumo del mese del chiamante sulla credenziale di institor (ADR 0023 §4).
   *
   * @returns Token usati, limite del piano e dettaglio per set.
   */
  getMyUsage(): Promise<MyUsage> {
    return this.client.request<MyUsage>('GET', '/api/v1/agent-sets/usage');
  }

  /**
   * Consumo del chiamante in crediti e per categoria, modello e giorno (ADR 0063).
   *
   * @param days Giorni della serie, da 7 a 370, oggi compreso.
   * @returns Periodo, limiti, crediti, giorni per modello e tabella dei prezzi.
   */
  getConsumption(days = 90): Promise<Consumption> {
    return this.client.request<Consumption>('GET', `/api/v1/agent-sets/usage/consumption?days=${String(days)}`);
  }

  /**
   * Consumo del mese di un utente; solo per l'amministratore della piattaforma.
   *
   * @param subjectId Utente.
   * @returns Token usati e dettaglio per set, senza limite (lo dà il piano).
   */
  getUserUsage(subjectId: string): Promise<UserUsage> {
    return this.client.request<UserUsage>(
      'GET',
      `/api/v1/agent-sets/usage/users/${encodeURIComponent(subjectId)}`,
    );
  }

  /**
   * Set posseduti e token del mese di ogni utente; solo per l'amministratore
   * della piattaforma (pagina Utenti, ADR 0030 tappa 7).
   *
   * @returns Il mese e una voce per utente con set o consumo.
   */
  getUsersUsage(): Promise<UsersUsage> {
    return this.client.request<UsersUsage>('GET', '/api/v1/agent-sets/usage/users');
  }

  /**
   * Consumo del mese di un set e del suo proprietario.
   *
   * @param setId Set.
   * @returns Token del set, del proprietario e limite se noto.
   */
  getSetUsage(setId: string): Promise<SetUsage> {
    return this.client.request<SetUsage>('GET', `${setsPath(setId)}/usage`);
  }
}
